import Close from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import { Grid, MenuItem, Typography } from "@mui/material";
import { FormTextField } from "components/atoms";
import { CommonModal, FormContainer, FormContent } from "components/molecules";
import { ECommonDialogType } from "components/molecules/CommonDialog";
import { ICommonModal } from "components/molecules/CommonModal";
import { useFormik, useFormikContext } from "formik";
import { AlertHelper, DialogHelper } from "helpers";
import { EAlertType } from "helpers/alertHelper";
import _ from "lodash";
import {
  EInProcessCheckStatus,
  EInprocessCheckType,
  EUnitPosition,
  IApiResponse,
  IInProcessCheck,
  IInProcessCheckDetail,
  IQualityConditionInProcessCheckDetail,
} from "model";
import { IUploadFileResponse } from "model/File";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "redux/configurations/reduxHooks";
import { FileApiService, InProcessCheckDetailApiService } from "services";
import { EVENTS_NAME } from "signalr/InprocessCheckSignalRService";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";
import { PhotoCaptureModal } from "./PhotoCaptureModal";
import { QualitySelect } from "./QualityConditionSelect";
import { canEditAfterReview, canPerformAndReview, deletePhoto } from "./helper";

export const PerformDetailModal = (
  props: Pick<ICommonModal, "open" | "handleClose"> & {
    processDetail?: IInProcessCheckDetail;
    lastProcessDetail?: IInProcessCheckDetail;
    onSuccess: (processDetail: IInProcessCheckDetail) => void;
    inProcessId: string;
  }
) => {
  const user = useAppSelector((state) => state.IdentityReducer.currentUser);
  const signalR = useAppSelector((state) => state.SignalRReducer.instance);
  const parentForm = useFormikContext<IInProcessCheck>();
  const isAdd = useMemo(() => {
    return !props.processDetail;
  }, [props.processDetail]);
  const isUnitType = useMemo(() => {
    return parentForm.values.type === EInprocessCheckType.UNIT;
  }, [parentForm.values.type]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenCamera, setIsOpenCamera] = useState<boolean>(false);
  const [selectedQualityIndex, setSelectedQualityIndex] = useState<number>();
  const [selectedImage, setSelectedImage] = useState<string>();

  const availableUnitPosition = useMemo(() => {
    if (isUnitType) {
      const availableOptions = _.difference(
        UNIT_POSITION_OPTIONS.map((e) => e.value),
        parentForm.values.inProcessCheckDetails?.map((e) => e.unitPosition) ||
          []
      );
      if (!isAdd) {
        availableOptions.unshift(props.processDetail!.unitPosition!);
      }
      return UNIT_POSITION_OPTIONS.filter((e) =>
        availableOptions.includes(e.value)
      );
    } else {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentForm.values.inProcessCheckDetails, props.processDetail]);

  const lateMinutes = useMemo<number>(() => {
    if (!props.lastProcessDetail || isUnitType || !isAdd) {
      return 0;
    } else if (
      moment
        .utc(props.lastProcessDetail.performedTime)
        .local()
        .add(35, "minute")
        .isBefore(moment(), "minute")
    ) {
      return Math.abs(
        moment
          .utc(props.lastProcessDetail.performedTime)
          .local()
          .add(35, "minute")
          .diff(moment(), "minute")
      );
    } else {
      return 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const shouldDisableEdit = useMemo(
    () =>
      canEditAfterReview()
        ? false
        : canPerformAndReview()
        ? parentForm.values.status === EInProcessCheckStatus.Reviewed
        : true,
    [parentForm.values.status]
  );

  const isFirstPerform = useMemo(
    () => {
      if (isAdd) {
        if (!props.lastProcessDetail) {
          return true;
        } else {
          return false;
        }
      } else if (
        parentForm.values.inProcessCheckDetails?.length === 1 ||
        (props.processDetail?.numberOfUnitFrom === 1 &&
          props.processDetail.numberOfUnitTo === 1)
      ) {
        return true;
      } else {
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.lastProcessDetail, isAdd]
  );

  const DEFAULT_NEW_PROCESS_DETAIL = useMemo<IInProcessCheckDetail>(
    () => {
      const defaultPosition = availableUnitPosition[0];
      const defaultNumberOfUnit = defaultPosition
        ? defaultPosition.value === EUnitPosition.HEAD
          ? 1
          : defaultPosition.value === EUnitPosition.BODY
          ? Math.floor(parentForm.values.totalQuantity! / 2)
          : parentForm.values.totalQuantity!
        : 0;
      return {
        performedTime: new Date(),
        numberOfUnitFrom: isUnitType
          ? defaultNumberOfUnit
          : (props.lastProcessDetail?.numberOfUnitTo || 0) + 1,
        numberOfUnitTo: isUnitType
          ? defaultNumberOfUnit
          : isFirstPerform
          ? 1
          : undefined,
        performedBy: user!.userName.trim(),
        inProcessCheckId: props.inProcessId,
        qualityCheckingConditions:
          parentForm.values.qualityCheckingConditionMetadata?.map<IQualityConditionInProcessCheckDetail>(
            (e) => ({
              name: e.name,
              value: "",
              imageRequired: e.imageRequired,
              nameEN: e.nameEN,
            })
          ) || [],
        imageUrls: [],
        unitPosition: isUnitType ? availableUnitPosition[0]?.value : undefined,
        isLate: lateMinutes > 0 ? true : false,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.open]
  );

  const formik = useFormik<IInProcessCheckDetail>({
    initialValues: DEFAULT_NEW_PROCESS_DETAIL,
    enableReinitialize: true,
    validationSchema: getProcessDetailValidationSchema(
      isUnitType,
      isFirstPerform
    ),
    onSubmit: (values: IInProcessCheckDetail) => {
      if (shouldDisableEdit) {
        props.handleClose?.();
      } else {
        if (values.numberOfUnitTo! > parentForm.values.totalQuantity!) {
          DialogHelper.open({
            title: I18nTranslator(
              translations.inProcessCheckReportDetail.unitToGreaterThanTotal
            ),
            message: I18nTranslator(
              translations.inProcessCheckReportDetail.doYouWantToContinue
            ),
            isCancel: true,
            onConfirm: () =>
              validateQualityCondition(values) && uploadAllPhoto(values),
            type: ECommonDialogType.WARNING,
          });
        } else {
          validateQualityCondition(values) && uploadAllPhoto(values);
        }
      }
    },
  });

  useEffect(() => {
    if (props.open && isAdd) {
      // pauseReminder(parentForm.values.id!);
      signalR?.invoke(EVENTS_NAME.PAUSE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    props.processDetail &&
      formik.setValues({
        ...props.processDetail,
        performedTime: moment
          .utc(props.processDetail.performedTime)
          .local()
          .toDate(),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.processDetail]);

  const onSavePhotoPress = (base64Image: string) => {
    const prevUrl =
      formik.values.qualityCheckingConditions[selectedQualityIndex!]?.imageUrl;
    formik.setFieldValue(
      `qualityCheckingConditions.${selectedQualityIndex}.imageUrl`,
      base64Image
    );
    formik.setFieldValue(
      `qualityCheckingConditions.${selectedQualityIndex}.prevImageUrl`,
      prevUrl
    );
    setIsOpenCamera(false);
    setSelectedQualityIndex(undefined);
    selectedImage && setSelectedImage(undefined);
  };

  const uploadAllPhoto = async (request: IInProcessCheckDetail) => {
    setIsLoading(true);
    try {
      const photosNeedToUpload = request.qualityCheckingConditions
        .map<TUploadPhotoRequest | null>((e, index) => {
          if (
            e.prevImageUrl ||
            (e.imageUrl && !e.imageUrl.startsWith("http"))
          ) {
            return { imageUrl: e.imageUrl!, index: index };
          } else {
            return null;
          }
        })
        .filter((e) => e !== null) as TUploadPhotoRequest[];
      const photosNeedToDelete = request.qualityCheckingConditions
        .map<string | null>((e) => e.prevImageUrl || null)
        .filter((e) => e !== null && e.startsWith("http")) as string[];
      if (photosNeedToUpload.length) {
        const result = await uploadPhoto(photosNeedToUpload);
        if (result !== false) {
          if (photosNeedToDelete.length)
            Promise.all(photosNeedToDelete.map((e) => deletePhoto(e)))
              .then((res) => {
                if (res.some((e) => !e)) {
                  AlertHelper.showAlert(
                    I18nTranslator(
                      translations.inProcessCheckReportDetail.deleteImageError
                    ),
                    EAlertType.WARNING
                  );
                }
              })
              .catch((error) => {
                console.log("Error", error);
              });
          result?.forEach((e, index) => {
            request.qualityCheckingConditions[
              photosNeedToUpload[index].index
            ].imageUrl = e.fileUrl!;
          });
        }
      }
      if (isAdd) {
        createInProcessDetail(request);
      } else {
        updateInProcessDetail(request);
      }
    } catch (error) {
      console.log("Error", error);
    }
  };

  const validateQualityCondition = (values: IInProcessCheckDetail) => {
    if (
      values.qualityCheckingConditions.every(
        (e) => e.value !== "" && e.value !== undefined && e.value !== null
      )
    ) {
      let imageValid = false;
      for (let i = 0; i < values.qualityCheckingConditions.length; ++i) {
        if (
          values.qualityCheckingConditions[i].imageRequired &&
          !values.qualityCheckingConditions[i].imageUrl
        ) {
          AlertHelper.showAlert(
            I18nTranslator(
              translations.inProcessCheckReportDetail
                .qualityConditionImageRequired
            ),
            EAlertType.ERROR
          );
          return false;
        } else {
          imageValid = true;
        }
      }
      if (imageValid) {
        return true;
      } else {
        return false;
      }
    } else {
      AlertHelper.showAlert(
        I18nTranslator(
          translations.inProcessCheckReportDetail.qualityConditionsRequired
        ),
        EAlertType.ERROR
      );
      return false;
    }
  };

  const createInProcessDetail = async (request: IInProcessCheckDetail) => {
    try {
      !isLoading && setIsLoading(true);
      const result =
        (await InProcessCheckDetailApiService.createInProcessCheckDetail({
          ...request,
          unitPosition: isUnitType ? request.unitPosition : undefined,
        })) as IApiResponse<IInProcessCheckDetail>;
      if (result.succeeded) {
        if (parentForm.values.type === EInprocessCheckType.TIME) {
          // result.data?.numberOfUnitTo! < parentForm.values.totalQuantity! &&
          //   postReminder(
          //     parentForm.values.id!,
          //     parentForm.values.productLot,
          //     parentForm.values.productCode,
          //     parentForm.values.productName!
          //   );
        }
        formik.resetForm({ values: DEFAULT_NEW_PROCESS_DETAIL });
        AlertHelper.showAlert(
          I18nTranslator(translations.inProcessCheckReportDetail.performAdded),
          EAlertType.SUCCESS
        );
        signalR?.playAlert();
        setTimeout(() => {
          signalR?.stopAlert();
        }, 100);
        props.onSuccess(result.data!);
      } else {
        AlertHelper.showAlert(
          result.message[0]?.Text ||
            I18nTranslator(translations.errorMessage.unknownError),
          EAlertType.ERROR
        );
        console.error("error", result);
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateInProcessDetail = async (request: IInProcessCheckDetail) => {
    try {
      !isLoading && setIsLoading(true);
      const result =
        (await InProcessCheckDetailApiService.updateInProcessCheckDetail({
          ...request,
          unitPosition: isUnitType ? request.unitPosition : undefined,
        })) as IApiResponse<IInProcessCheckDetail>;
      if (result.succeeded) {
        props.onSuccess(result.data!);
        AlertHelper.showAlert(
          I18nTranslator(
            translations.inProcessCheckReportDetail.performUpdated
          ),
          EAlertType.SUCCESS
        );
      } else {
        console.error("error", result);
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadPhoto = async (images: TUploadPhotoRequest[]) => {
    !isLoading && setIsLoading(true);
    const fetchedImage = await Promise.all(
      images.map((e) => fetch(e.imageUrl))
    );
    const blobs = await Promise.all(fetchedImage.map((e) => e.blob()));
    const file = blobs.map(
      (blob) =>
        new File([blob], `${moment().format("YYYY_MM_DDTHH_mm_ss")}.jpeg`)
    );
    try {
      const result = (await FileApiService.upload(file)) as IApiResponse<
        IUploadFileResponse[]
      >;
      if (result.succeeded) {
        images.forEach((e, index) => {
          formik.setFieldValue(
            `qualityCheckingConditions.${e.index}.imageUrl`,
            result.data![index].fileUrl
          );
        });

        return result.data;
      } else {
        AlertHelper.showError(result);
        return false;
      }
    } catch (error) {
      console.log("Network Error", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CommonModal
      {...props}
      title={
        isAdd
          ? I18nTranslator(
              translations.inProcessCheckReportDetail.addPerformance
            )
          : shouldDisableEdit
          ? I18nTranslator(translations.inProcessCheckReportDetail.performances)
          : I18nTranslator(
              translations.inProcessCheckReportDetail.updatePerformance
            )
      }
      handleClose={() => {
        isAdd &&
          parentForm.values.inProcessCheckDetails?.length &&
          // resumeReminder(parentForm.values.id!);
          signalR?.invoke(EVENTS_NAME.RESUME);
        props.handleClose?.();
      }}
    >
      <FormContainer
        formik={formik}
        loading={isLoading}
        isAdd={isAdd}
        // saveButtonTitle={
        //   shouldDisableEdit
        //     ? I18nTranslator(translations.button.close)
        //     : undefined
        // }
        hideDefaultSaveButton
        hideDefaultConfirmButton
        renderButtons={
          <Grid item>
            <LoadingButton
              variant="contained"
              type={"submit"}
              loading={isLoading}
              startIcon={shouldDisableEdit ? <Close /> : <SaveIcon />}
            >
              {shouldDisableEdit
                ? I18nTranslator(translations.button.close)
                : isAdd
                ? I18nTranslator(translations.button.create)
                : I18nTranslator(translations.button.save)}
            </LoadingButton>
          </Grid>
        }
      >
        {lateMinutes ? (
          <Typography color="red" marginBottom="16px" fontWeight="bold">
            {I18nTranslator(
              translations.inProcessCheckReportDetail.thisPerformIsLate,
              { minutes: lateMinutes }
            )}
          </Typography>
        ) : !isAdd && formik.values.isLate ? (
          <Typography color="red" marginBottom="16px" fontWeight="bold">
            {I18nTranslator(
              translations.inProcessCheckReportDetail.latePerform
            )}
          </Typography>
        ) : null}
        <FormContent>
          <Grid xs={6} item>
            <FormTextField
              label={I18nTranslator(
                translations.inProcessCheckReportDetail.date
              )}
              id="performedTime"
              renderValue={(value) => moment(value).format("DD/MM/YYYY")}
              disabled
            />
          </Grid>
          <Grid xs={6} item>
            <FormTextField
              label={I18nTranslator(
                translations.inProcessCheckReportDetail.time
              )}
              id="performedTime"
              renderValue={(value) => moment(value).format("HH:mm")}
              disabled
            />
          </Grid>
          {formik.values.qualityCheckingConditions?.map((e, index) => (
            <Grid item xs={12} container key={index}>
              <QualitySelect
                title={e.name}
                titleEn={e.nameEN}
                image={e.imageUrl}
                value={e.value.toString()}
                type={
                  parentForm.values.qualityCheckingConditionMetadata![index]
                    .type
                }
                onAttachImageClick={() => {
                  setIsOpenCamera(true);
                  setSelectedQualityIndex(index);
                }}
                onEditImageClick={() => {
                  setIsOpenCamera(true);
                  setSelectedQualityIndex(index);
                }}
                onViewImageClick={() => {
                  setIsOpenCamera(true);
                  setSelectedImage(
                    formik.values.qualityCheckingConditions![index].imageUrl
                  );
                  setSelectedQualityIndex(index);
                }}
                loading={isLoading}
                disabled={shouldDisableEdit}
                index={index}
                imageRequired={e.imageRequired}
              />
            </Grid>
          ))}
          <UnitSelect
            isUnitType={isUnitType}
            totalQuantity={parentForm.values.totalQuantity!}
            avaiablePosition={availableUnitPosition}
            disabled={
              isUnitType
                ? shouldDisableEdit
                : isFirstPerform || shouldDisableEdit
            }
          />
        </FormContent>
      </FormContainer>
      <PhotoCaptureModal
        onClose={() => {
          setIsOpenCamera(false);
          setSelectedQualityIndex(undefined);
          selectedImage && setSelectedImage(undefined);
        }}
        open={isOpenCamera}
        onSavePhoto={onSavePhotoPress}
        selectedImage={selectedImage}
      />
    </CommonModal>
  );
};

const UnitSelect = (props: {
  isUnitType: boolean;
  totalQuantity: number;
  avaiablePosition: typeof UNIT_POSITION_OPTIONS;
  disabled?: boolean;
}) => {
  const formik = useFormikContext<IInProcessCheckDetail>();

  useEffect(() => {
    if (props.isUnitType && _.isNil(formik.values.unitPosition)) {
      let value = 0;
      switch (formik.values.unitPosition!) {
        case EUnitPosition.HEAD: {
          value = 1;
          break;
        }
        case EUnitPosition.BODY: {
          value = Math.floor(props.totalQuantity / 2);
          break;
        }
        case EUnitPosition.TAIL: {
          value = props.totalQuantity;
          break;
        }
      }
      formik.setFieldValue("numberOfUnitFrom", value);
      formik.setFieldValue("numberOfUnitTo", value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.unitPosition]);

  useEffect(() => {
    if (props.isUnitType) {
      formik.setFieldValue("numberOfUnitFrom", formik.values.numberOfUnitTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.numberOfUnitTo]);

  return !props.isUnitType ? (
    <>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(
            translations.inProcessCheckReportDetail.fromUnit
          )}
          id="numberOfUnitFrom"
          disabled
        />
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.inProcessCheckReportDetail.toUnit)}
          id="numberOfUnitTo"
          required
          type="number"
          disabled={props.disabled}
        />
      </Grid>
    </>
  ) : (
    <>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(
            translations.inProcessCheckReportDetail.unitPosition
          )}
          id="unitPosition"
          select
          disabled={props.disabled}
          required
        >
          {props.avaiablePosition.map((e) => (
            <MenuItem value={e.value}>{I18nTranslator(e.title)}</MenuItem>
          ))}
        </FormTextField>
      </Grid>
      <Grid xs={6} item>
        <FormTextField
          label={I18nTranslator(translations.inProcessCheckReportDetail.unit)}
          id="numberOfUnitTo"
          required
          type="number"
          disabled={props.disabled}
        />
      </Grid>
    </>
  );
};

const getProcessDetailValidationSchema = (
  isUnitType: boolean,
  isFirstPerform: boolean
) =>
  yup
    .object()
    .shape<
      Record<
        keyof Pick<
          IInProcessCheckDetail,
          "performedTime" | "numberOfUnitTo" | "performedBy" | "unitPosition"
        >,
        yup.AnySchema
      >
    >({
      performedTime: yup.date().required(translations.yupError.required),
      numberOfUnitTo: yup
        .number()
        .typeError(translations.yupError.formatNotValid)
        .integer(translations.yupError.formatNotValid)
        .required(translations.yupError.required)
        .test(
          "to_unit_must_bigger",
          translations.inProcessCheckReportDetail.toUnitMustBigger,
          (value, context) => {
            if (isUnitType) {
              return true;
            } else if (
              value &&
              context.parent.numberOfUnitFrom &&
              value <= context.parent.numberOfUnitFrom
            ) {
              if (isFirstPerform) {
                return true;
              } else {
                return false;
              }
            } else {
              return true;
            }
          }
        ),
      // .test(
      //   "to_unit_must_smaller_than_total",
      //   translations.inProcessCheckReportDetail.toUnitMustNotExceed,
      //   (value, context) => {
      //     return isUnitType ? true : value && value > totalQty ? false : true;
      //   }
      // ),
      performedBy: yup.string().required(translations.yupError.required),
      unitPosition: isUnitType
        ? yup.number().required(translations.yupError.required)
        : yup.mixed(),
      // ...qualityConditions.reduce((object, currentValue, index) => {
      //   return {
      //     ...object,
      //     [`qualityCheckingConditions.${index}.value`]: yup.string().required(),
      //   };
      // }, {}),
    });

const UNIT_POSITION_OPTIONS: { title: string; value: EUnitPosition }[] = [
  {
    title: translations.inProcessCheckReportDetail.positionHead,
    value: EUnitPosition.HEAD,
  },
  {
    title: translations.inProcessCheckReportDetail.positionBody,
    value: EUnitPosition.BODY,
  },
  {
    title: translations.inProcessCheckReportDetail.positionTail,
    value: EUnitPosition.TAIL,
  },
];

type TUploadPhotoRequest = { imageUrl: string; index: number };
